class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => { if (event.target.id === 'CartDrawer-Overlay') this.close(); });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || document.querySelector('.cc-root')) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      this.refresh();
    }
    this.inert = false;
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden', 'koppa-cart-open');
    this.inertBackground ||= Array.from(document.body.children).filter(element => element !== this && !element.contains(this) && !element.inert);
    this.inertBackground.forEach(element => { element.inert = true; });
    const focusDrawer = () => {
      if (this.classList.contains('active') && !this.contains(document.activeElement)) {
        trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close') || this.querySelector('.drawer__inner'));
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(focusDrawer));
    this.addEventListener('transitionend', focusDrawer, {once: true});
  }

  close() {
    this.classList.remove('active');
    this.inert = true;
    (this.inertBackground || []).forEach(element => { if (element.isConnected) element.inert = false; });
    this.inertBackground = null;
    document.body.classList.remove('overflow-hidden', 'koppa-cart-open');
    removeTrapFocus(this.activeElement);
  }

  async refresh() {
    try {
      const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
      if (!response.ok) throw new Error();
      const sections = await response.json();
      this.getSectionsToRender().forEach(section => {
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (target && sections[section.id]) target.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      });
      this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
      if (this.classList.contains('active')) trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close') || this.querySelector('.drawer__inner'));
    } catch (_) {
      const error = this.querySelector('#CartDrawer-CartErrors');
      if (error) error.textContent = window.cartStrings.error;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    try {
      const sections = this.getSectionsToRender().map((section) => ({
        target: section.selector ? document.querySelector(section.selector) : document.getElementById(section.id),
        html: this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector),
      }));
      sections.forEach(({ target, html }) => { if (target) target.innerHTML = html; });
    } catch (_) {
      await this.refresh();
    }
    this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
    const notice = this.querySelector('.kc-added');
    if (notice) notice.hidden = false;
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  const pending = new WeakSet();
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented || form.closest('product-form')) return;
    const action = new URL(form.action, location.href);
    if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const data = new FormData(form);
    if (!data.get('id') && !data.has('items[0][id]')) return;
    event.preventDefault();
    if (pending.has(form)) return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    data.set('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
    data.set('sections_url', location.pathname);
    let status = form.querySelector('[data-koppa-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.koppaCartStatus = '';
      status.setAttribute('role', 'alert');
      form.append(status);
    }
    status.hidden = true;
    pending.add(form);
    const disabled = button?.disabled;
    // Plain-text buttons say "Adding…" at once, so the press registers before the round trip.
    const label = button && !button.children.length && window.koppaCartStrings?.adding ? button.textContent : null;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); if (label !== null) button.textContent = window.koppaCartStrings.adding; }
    drawer.setActiveElement(button || document.activeElement);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || window.cartStrings.error);
      await drawer.renderContents(result);
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form', productVariantId: data.get('id'), cartData: result,
      });
    } catch (error) {
      status.textContent = error.message || window.cartStrings.error;
      status.hidden = false;
    } finally {
      pending.delete(form);
      if (button) { button.disabled = disabled; button.removeAttribute('aria-busy'); if (label !== null) button.textContent = label; }
    }
  });
})();

// Checkout goes straight to /checkout instead of POSTing the cart form.
// Quantities are already saved by AJAX, and the form post failed silently in
// two ways: native validation on a quantity input (a rejected value keeps its
// custom validity, and the input is often off screen or inside a popover, so
// the browser blocks the submit with no visible message), and a submit made
// while a cart update was in flight, which koppa-cart-approved.js cancels. A
// stale form could also post outdated quantities and bounce the shopper back
// to /cart. The submit button stays as the no-JS fallback.
(() => {
  const checkoutUrl = () => (window.Shopify?.routes?.root || '/') + 'checkout';
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const dirty = scope => [...scope.querySelectorAll('input[name="updates[]"]')].some(input => input.value !== input.getAttribute('value'));
  const busy = scope => [...scope.querySelectorAll('cart-items, cart-drawer-items'), scope].some(el => el.updating);
  document.addEventListener('click', async event => {
    const button = event.target.closest?.('button[name="checkout"]');
    if (!button || button.disabled || event.defaultPrevented) return;
    const form = button.form;
    if (!form || !/\/cart\/?$/.test(new URL(form.action, location.href).pathname)) return;
    event.preventDefault();
    if (button.dataset.koppaCheckout) return;
    button.dataset.koppaCheckout = 'true';
    button.setAttribute('aria-busy', 'true');
    const scope = form.closest('cart-drawer, cart-items') || document;
    // Let a just-edited quantity reach the server first (change debounce + request), capped so checkout never hangs.
    const deadline = Date.now() + 4000;
    if (dirty(scope)) await wait((typeof ON_CHANGE_DEBOUNCE_TIMER === 'number' ? ON_CHANGE_DEBOUNCE_TIMER : 300) + 100);
    while ((busy(scope) || dirty(scope)) && Date.now() < deadline) await wait(100);
    const note = form.querySelector('textarea[name="note"]');
    if (note) await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({note: note.value}),
    }).catch(() => {});
    location.assign(checkoutUrl());
    // bfcache: a shopper who comes back from checkout gets a working button.
    window.addEventListener('pageshow', () => { delete button.dataset.koppaCheckout; button.removeAttribute('aria-busy'); }, {once: true});
  });
})();
